import { Modal } from "antd";
import { useOrderingMessage } from "../../useOrderingMessage";
import { PercentageOutlined } from "@ant-design/icons";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getApiErrorMessage } from "../../../../../utils/translations";
import { posModalStyles as modalStyles, ModalHeader } from "../../modalPrimitives";
import { ItemDiscountRow } from "./ItemDiscountRow";
import { DiscountPercentPicker } from "./DiscountPercentPicker";
import { SelectAllBar } from "./SelectAllBar";
import { ItemDiscountsFooter } from "./ItemDiscountsFooter";
import type { RowPercentChoice } from "./RowPercentOptions";
import {
  buildDiscountRows,
  mostCommonPercent,
  planRowDiscount,
  type DiscountRow,
} from "./itemDiscountMath";
import { useApplyItemDiscounts } from "../../../../../hooks/useBills";
import { useViewport } from "../../../../../hooks/useViewport";
import type { BillOut } from "../../../../../api/bills";

interface ItemDiscountsModalProps {
  open: boolean;
  onClose: () => void;
  billId: number | null;
  bill: BillOut | undefined;
}

/** Общият процент при отваряне без отстъпки — най-честата е на два клика. */
const DEFAULT_PERCENT = 10;

/** id на ред → стойност. Ред без запис не участва / следва общата. */
type PerRow = Record<number, number>;

/** Отстъпки по сметка — БЕЗ предварително избран продукт. Модалът показва
 *  КРАЙНОТО състояние на всеки ред: избраните бройки (степер −N/max+) ще са с
 *  процента на реда, а неизбраните — без отстъпка. Процентът на реда е общият
 *  горе или собствен (chip-ът на реда → „За този ред"), затова една сметка
 *  събира няколко различни отстъпки и редове без отстъпка на един запис.
 *  Праща се само разликата спрямо сегашното (`planRowDiscount`). */
export const ItemDiscountsModal = ({
  open,
  onClose,
  billId,
  bill,
}: ItemDiscountsModalProps) => {
  const { t, i18n } = useTranslation();
  const message = useOrderingMessage();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const applyItemDiscountsMutation = useApplyItemDiscounts();

  const [generalPercent, setGeneralPercent] = useState(DEFAULT_PERCENT);
  const [selectedQuantities, setSelectedQuantities] = useState<PerRow>({});
  const [ownPercents, setOwnPercents] = useState<PerRow>({});
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const rows = useMemo(
    () => buildDiscountRows(bill, i18n.language, t("common.product")),
    [bill, i18n.language, t],
  );
  const selectableRows = useMemo(
    () => rows.filter((row) => !row.locked),
    [rows],
  );

  // При отваряне редовете с отстъпка са избрани с цялото си количество.
  // Общият процент е най-честият им; редовете с друг процент го пазят като
  // собствен, за да не ги пренапише смяна на общия.
  useEffect(() => {
    if (!open) return;
    const general = mostCommonPercent(rows) ?? DEFAULT_PERCENT;
    const discountedRows = rows.filter((row) => row.currentPercent > 0);
    setGeneralPercent(general);
    setSelectedQuantities(
      Object.fromEntries(
        discountedRows.map((row) => [row.billElementItemId, row.quantity]),
      ),
    );
    setOwnPercents(
      Object.fromEntries(
        discountedRows
          .filter((row) => row.currentPercent !== general)
          .map((row) => [row.billElementItemId, row.currentPercent]),
      ),
    );
    setExpandedRowId(null);
    // Само при отваряне — опресняването на сметката след запис не бива да
    // пренаписва избора, докато модалът се затваря.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selectedQtyOf = (row: DiscountRow) =>
    selectedQuantities[row.billElementItemId] ?? 0;
  const hasOwnPercent = (row: DiscountRow) =>
    ownPercents[row.billElementItemId] !== undefined;
  const percentOf = (row: DiscountRow) =>
    ownPercents[row.billElementItemId] ?? generalPercent;

  const setQuantity = (row: DiscountRow, quantity: number) =>
    setSelectedQuantities((prev) => ({
      ...prev,
      [row.billElementItemId]: Math.max(0, Math.min(row.quantity, quantity)),
    }));

  const changeQuantity = (row: DiscountRow, delta: number) =>
    setQuantity(row, selectedQtyOf(row) + delta);

  // Изборът от „За този ред": „Без" маха реда от отстъпката, процент (или
  // „Обща") го избира с цялото количество, ако още не е избран.
  const pickRowPercent = (row: DiscountRow, choice: RowPercentChoice) => {
    const id = row.billElementItemId;
    setOwnPercents((prev) => {
      const next = { ...prev };
      if (choice === null || choice === 0) delete next[id];
      else next[id] = choice;
      return next;
    });
    if (choice === 0) setQuantity(row, 0);
    else if (selectedQtyOf(row) === 0) setQuantity(row, row.quantity);
    setExpandedRowId(null);
  };

  const allSelected =
    selectableRows.length > 0 &&
    selectableRows.every((row) => selectedQtyOf(row) >= row.quantity);

  // Собствените проценти остават — повторното „Избери всички" ги връща.
  const toggleSelectAll = () =>
    setSelectedQuantities(
      allSelected
        ? {}
        : Object.fromEntries(
            selectableRows.map((row) => [row.billElementItemId, row.quantity]),
          ),
    );

  const removeAll = () => {
    setSelectedQuantities({});
    setOwnPercents({});
    setExpandedRowId(null);
  };

  const plans = rows.map((row) =>
    planRowDiscount(row, selectedQtyOf(row), percentOf(row)),
  );
  const changedLines = plans.flatMap((plan) => plan.lines);
  // „Преди" е ВИНАГИ сумата по менюто; „след" — това, което ще се плати.
  const totalBefore = rows.reduce((sum, row) => sum + row.menuTotal, 0);
  const totalAfter = plans.reduce((sum, plan) => sum + plan.total, 0);

  const selectedRows = selectableRows.filter((row) => selectedQtyOf(row) > 0);
  const discountedCount = selectedRows.filter(
    (row) => percentOf(row) > 0,
  ).length;
  const ownPercentCount = selectedRows.filter(hasOwnPercent).length;

  const canSave = !!billId && changedLines.length > 0;

  const handleSave = useCallback(async () => {
    if (!billId || changedLines.length === 0) return;
    setSaving(true);
    try {
      await applyItemDiscountsMutation.mutateAsync({
        billId,
        data: { billId, lines: changedLines },
      });
      message.success(t("itemDiscounts.applySuccess"));
      onClose();
    } catch (err: unknown) {
      message.error(getApiErrorMessage(err, t("common.error")));
    } finally {
      setSaving(false);
    }
  }, [billId, changedLines, applyItemDiscountsMutation, message, t, onClose]);

  // Esc не прескача нива: първо свива разгънатия ред, после затваря модала
  // (затова antd `keyboard` е изключен). Enter записва — само на десктоп.
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // preventDefault = „Esc е консумиран" за глобалната Esc-навигация.
        e.preventDefault();
        if (expandedRowId !== null) setExpandedRowId(null);
        else onClose();
      } else if (e.key === "Enter" && isDesktop) {
        e.preventDefault();
        if (canSave && !saving) handleSave();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, isDesktop, expandedRowId, canSave, saving, handleSave, onClose]);

  const bodyPadClass = isNarrow ? "px-4" : "px-6";

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      keyboard={false}
      destroyOnHidden
      width={isMobile ? undefined : isTablet ? 520 : 640}
      styles={modalStyles}
      className="max-w-[calc(100vw-32px)]"
    >
      <div className="flex max-h-[88vh] flex-col bg-pos-card text-pos-ink">
        <ModalHeader
          icon={<PercentageOutlined />}
          title={t("itemDiscounts.title")}
          subtitle={t("itemDiscounts.subtitle", {
            name: bill?.name ?? "",
            total: parseFloat(bill?.total_amount ?? "0").toFixed(2),
          })}
          onClose={onClose}
          isNarrow={isNarrow}
        />

        {/* Общият процент — за избраните редове без собствен */}
        <div className={`${bodyPadClass} border-b border-pos-line2 bg-pos-panel py-4`}>
          <DiscountPercentPicker
            percent={generalPercent}
            onChange={setGeneralPercent}
            autoFocus={open && isDesktop}
            ownPercentCount={ownPercentCount}
            isNarrow={isNarrow}
          />
        </div>

        <SelectAllBar
          allSelected={allSelected}
          disabled={selectableRows.length === 0}
          discountedCount={discountedCount}
          selectableCount={selectableRows.length}
          canRemoveAll={selectedRows.length > 0}
          onToggleSelectAll={toggleSelectAll}
          onRemoveAll={removeAll}
          isNarrow={isNarrow}
        />

        <div
          className={`${bodyPadClass} flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto py-3`}
        >
          {rows.length === 0 && (
            <p className="m-0 py-6 text-center text-sm text-pos-muted">
              {t("itemDiscounts.emptyBill")}
            </p>
          )}
          {rows.map((row) => (
            <ItemDiscountRow
              key={row.billElementItemId}
              row={row}
              selectedQty={selectedQtyOf(row)}
              percent={percentOf(row)}
              hasOwnPercent={hasOwnPercent(row)}
              generalPercent={generalPercent}
              expanded={expandedRowId === row.billElementItemId}
              onToggleExpanded={() =>
                setExpandedRowId((current) =>
                  current === row.billElementItemId
                    ? null
                    : row.billElementItemId,
                )
              }
              onPickPercent={(choice) => pickRowPercent(row, choice)}
              onQuantityChange={(delta) => changeQuantity(row, delta)}
              lockedHint={t("ordering.lockedPriceNoDiscount")}
              customPriceHint={t("itemDiscounts.customPriceHint")}
              isNarrow={isNarrow}
            />
          ))}
        </div>

        <ItemDiscountsFooter
          totalBefore={totalBefore}
          totalAfter={totalAfter}
          canSave={canSave}
          saving={saving}
          onBack={onClose}
          onSave={handleSave}
          isNarrow={isNarrow}
          isDesktop={isDesktop}
        />
      </div>
    </Modal>
  );
};
